import { motion, useReducedMotion } from 'framer-motion';

const statusColor = (pct: number) => {
  if (pct >= 1) return 'var(--premium-success)';
  if (pct >= 0.85) return 'var(--premium-warning)';
  if (pct > 0) return 'var(--color-accent)';
  return 'var(--premium-muted)';
};

export function StatRing({
  value,
  max,
  label,
  display,
  unit,
  size = 76,
  className = '',
}: {
  value: number;
  max: number;
  label: string;
  display?: string;
  unit?: string;
  size?: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  const pct = max > 0 ? Math.min(1, value / max) : 0;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const color = statusColor(pct);
  const center = display ?? (unit ? `${Math.round(value)}` : `${Math.round(value)}`);

  return (
    <div className={`flex flex-col items-center gap-1.5 ${className}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--premium-ring-track)"
            strokeWidth={stroke}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circ}
            initial={reduceMotion ? { strokeDashoffset: circ - dash } : { strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ - dash }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="font-display text-lg font-bold tabular-nums leading-none text-[var(--text-primary)]">{center}</span>
          {unit ? <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{unit}</span> : null}
        </div>
      </div>
      <span className="max-w-[4.5rem] text-center text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </span>
    </div>
  );
}
